import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCodeOptions, createCodeOption, updateCodeOption, deleteCodeOption } from '../../store/actions/codeActions';
import { toast } from 'sonner';
import { Btn, BmModal, Card, ConfirmModal, EmptyState, ErrorState, Field, Input, Loading } from '../ForBrands/ui/BrandUI';
import { AddRow, CardHead, IconButton } from './codeUi';

// Admin Mode > Codes, left card: every code option as a selectable row, an
// add row on top, and edit and delete on each row. Edits open a BmModal and
// a delete asks first, because it also deletes every code in the option.

const cx = (...parts) => parts.filter(Boolean).join(' ');

const CodeOptionsTable = ({ onSelectOption, selectedId }) => {
  const dispatch = useDispatch();
  const { codeOptions } = useSelector((state) => state.codes);
  const [newOptionName, setNewOptionName] = useState('');
  const [loading, setLoading] = useState(true);

  const [editingOption, setEditingOption] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [deletingOption, setDeletingOption] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.resolve(dispatch(fetchCodeOptions())).finally(() => {
      if (live) setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [dispatch]);

  const handleAddOption = () => {
    if (!newOptionName.trim()) return;
    dispatch(createCodeOption({ name: newOptionName }));
    setNewOptionName('');
    toast.success('Code option created');
  };

  const handleUpdateOption = (option) => {
    setEditingOption(option);
    setEditedName(option.name);
  };

  const handleDeleteOption = (option) => {
    dispatch(deleteCodeOption(option)); // option = { _id: ..., name: ... }
    onSelectOption(null);
    toast.success('Code option deleted');
  };

  const handleConfirmUpdate = () => {
    if (!editedName.trim())
      return;
    dispatch(updateCodeOption({ ...editingOption, name: editedName }));
    setEditingOption(null); // close dialog
    toast.success('Code option updated');
  };

  // The store holds a list; anything else means the load went wrong.
  const loadFailed = codeOptions != null && !Array.isArray(codeOptions);
  const options = Array.isArray(codeOptions) ? codeOptions : [];

  let body;
  if (loadFailed) {
    body = <ErrorState>Could not load the code options. Refresh the page to try again.</ErrorState>;
  } else if (loading && options.length === 0) {
    body = <Loading label="Loading code options..." />;
  } else if (options.length === 0) {
    body = <EmptyState>No code options yet. Add the first one above.</EmptyState>;
  } else {
    body = (
      <ul aria-label="Code options" className="overflow-hidden rounded-[8px] border border-bm-border">
        {options.map((option) => {
          const active = option._id === selectedId;
          return (
            <li
              key={option._id}
              className={cx(
                'flex items-center gap-1 border-b border-bm-border pr-2 transition-colors last:border-b-0',
                active ? 'bg-bm-subtle' : 'bg-white hover:bg-bm-subtle',
              )}
            >
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onSelectOption(option)}
                className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3 pr-2 text-left"
              >
                <span aria-hidden="true" className={cx('h-5 w-[3px] shrink-0 rounded-full', active ? 'bg-bm-orange' : 'bg-transparent')} />
                <span className={cx('min-w-0 flex-1 truncate text-bm-ink', active ? 'bm-label-l' : 'bm-body-m')}>{option.name}</span>
              </button>
              <IconButton icon="edit" label={`Edit ${option.name}`} onClick={() => handleUpdateOption(option)} />
              <IconButton icon="trash" label={`Delete ${option.name}`} danger onClick={() => setDeletingOption(option)} />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Card padding="p-5" className="flex min-w-0 flex-col gap-4">
      <CardHead title="Code options" count={loadFailed || loading ? undefined : options.length}>
        Select an option to manage its codes.
      </CardHead>

      <AddRow
        value={newOptionName}
        onChange={setNewOptionName}
        onAdd={handleAddOption}
        placeholder="New code option name"
        buttonLabel="Add option"
      />

      {body}

      {/* Edit Code Option Dialog */}
      {editingOption && (
        <BmModal
          title="Edit code option"
          width={460}
          onClose={() => setEditingOption(null)}
          footer={
            <>
              <Btn variant="secondary" className="ml-auto" onClick={() => setEditingOption(null)}>
                Cancel
              </Btn>
              <Btn onClick={handleConfirmUpdate} disabled={!editedName.trim()}>
                Save
              </Btn>
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmUpdate();
            }}
          >
            <Field label="Name">
              <Input autoFocus value={editedName} onChange={(e) => setEditedName(e.target.value)} />
            </Field>
            <p className="mt-2 bm-body-s text-bm-muted">The codes in this option take the new name as their type.</p>
          </form>
        </BmModal>
      )}

      {deletingOption && (
        <ConfirmModal
          tone="danger"
          title="Delete code option"
          body={`Delete "${deletingOption.name}"? Every code in it is deleted too, and this cannot be undone.`}
          confirmLabel="Delete option"
          onClose={() => setDeletingOption(null)}
          onConfirm={() => {
            handleDeleteOption(deletingOption);
            setDeletingOption(null);
          }}
        />
      )}
    </Card>
  );
};

export default CodeOptionsTable;
